import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Sidebar } from "@/components/layout/sidebar";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";

interface MobileNavProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

interface BottomTab {
  readonly label: string;
  readonly href: string;
  readonly icon: string;
  readonly minRole: string;
}

const BOTTOM_TABS: ReadonlyArray<BottomTab> = [
  { label: "ראשי", href: "/", icon: "📊", minRole: "viewer" },
  { label: "הזמנות", href: "/orders", icon: "📦", minRole: "viewer" },
  { label: "מוצרים", href: "/products", icon: "🌸", minRole: "viewer" },
  { label: "עוד", href: "__more__", icon: "⋯", minRole: "viewer" },
];

interface MoreItem {
  readonly label: string;
  readonly href: string;
  readonly icon: string;
  readonly minRole: string;
}

const MORE_ITEMS: ReadonlyArray<MoreItem> = [
  { label: "קופונים", href: "/coupons", icon: "🎟️", minRole: "employee" },
  { label: "ביקורות", href: "/reviews", icon: "⭐", minRole: "employee" },
  { label: "סבסקריפציות", href: "/subscriptions", icon: "🔄", minRole: "employee" },
  { label: "חלונות קופצים", href: "/popups", icon: "🎯", minRole: "manager" },
  { label: "לוח שנה", href: "/booking/calendar", icon: "📅", minRole: "viewer" },
  { label: "תורים", href: "/booking/appointments", icon: "⏰", minRole: "viewer" },
  { label: "שירותים", href: "/booking/services", icon: "🛎️", minRole: "employee" },
  { label: "פוקסי AI", href: "/foxy", icon: "💬", minRole: "viewer" },
  { label: "כללי", href: "/settings", icon: "⚙️", minRole: "manager" },
  { label: "צוות", href: "/team", icon: "👥", minRole: "manager" },
  { label: "משפטי", href: "/legal", icon: "⚖️", minRole: "manager" },
  { label: "חיוב", href: "/billing", icon: "💳", minRole: "owner" },
];

interface MoreSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

function MoreSheet({ open, onClose }: MoreSheetProps): ReactNode {
  const location = useLocation();
  const { hasRole } = useAuth();

  const visibleItems = MORE_ITEMS.filter((item) => hasRole(item.minRole));

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[60]"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed bottom-0 start-0 end-0 z-[61] flex flex-col"
        style={{
          background: "linear-gradient(180deg, #1A1035 0%, #0F172A 100%)",
          borderRadius: "24px 24px 0 0",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 68px)",
          maxHeight: "75vh",
          animation: "moreSheetIn 0.25s cubic-bezier(0.32, 0.72, 0, 1)",
        }}
        role="dialog"
        aria-label="תפריט נוסף"
      >
        <div className="flex justify-center pt-3 pb-2">
          <div
            className="rounded-full"
            style={{
              width: "36px",
              height: "4px",
              background: "rgba(255,255,255,0.2)",
            }}
          />
        </div>

        <p
          className="px-5 pb-3 text-sm"
          style={{
            fontFamily: "Rubik, sans-serif",
            fontWeight: 700,
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
          }}
        >
          עוד
        </p>

        <div className="overflow-y-auto px-3">
          <div className="grid grid-cols-4 gap-2 pb-4">
            {visibleItems.map((item) => {
              const isActive = location.pathname.startsWith(item.href);
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className="flex flex-col items-center gap-1 rounded-xl p-3 transition-all duration-150"
                  style={
                    isActive
                      ? {
                          background: "rgba(124,58,237,0.2)",
                          color: "#fff",
                        }
                      : {
                          color: "rgba(255,255,255,0.6)",
                        }
                  }
                >
                  <span className="text-2xl leading-none">{item.icon}</span>
                  <span
                    className="text-center leading-tight"
                    style={{
                      fontFamily: "Rubik, sans-serif",
                      fontWeight: 500,
                      fontSize: "0.7rem",
                    }}
                  >
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes moreSheetIn {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
}

export function MobileNav({ open, onOpenChange }: MobileNavProps): ReactNode {
  const location = useLocation();
  const { hasRole } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    onOpenChange(false);
  }, [location.pathname, onOpenChange]);

  const visibleTabs = BOTTOM_TABS.filter((tab) => hasRole(tab.minRole));

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-[272px] p-0 border-0"
          style={{ background: "transparent" }}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>תפריט ניווט</SheetTitle>
          </SheetHeader>
          <Sidebar />
        </SheetContent>
      </Sheet>

      <nav
        className="fixed bottom-0 start-0 end-0 z-50 flex md:hidden items-stretch"
        style={{
          height: "calc(68px + env(safe-area-inset-bottom, 0px))",
          background: "linear-gradient(180deg, #1A1035 0%, #0F172A 100%)",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}
        aria-label="ניווט תחתי"
      >
        {visibleTabs.map((tab) => {
          if (tab.href === "__more__") {
            return (
              <button
                key="more"
                type="button"
                onClick={() => setMoreOpen((prev) => !prev)}
                className="flex flex-1 flex-col items-center justify-center gap-1 transition-colors"
                style={{ color: moreOpen ? "#fff" : "rgba(255,255,255,0.5)" }}
                aria-label="עוד אפשרויות"
              >
                {moreOpen && (
                  <span
                    className="absolute top-0 rounded-b-full"
                    style={{
                      width: "28px",
                      height: "3px",
                      background:
                        "linear-gradient(90deg, #7C3AED 0%, #EC4899 100%)",
                    }}
                  />
                )}
                <span className="text-xl leading-none">{tab.icon}</span>
                <span
                  style={{
                    fontFamily: "Rubik, sans-serif",
                    fontWeight: 500,
                    fontSize: "0.65rem",
                  }}
                >
                  {tab.label}
                </span>
              </button>
            );
          }

          const isActive =
            tab.href === "/"
              ? location.pathname === "/" || location.pathname === ""
              : location.pathname.startsWith(tab.href);

          return (
            <NavLink
              key={tab.href}
              to={tab.href}
              className={cn(
                "relative flex flex-1 flex-col items-center justify-center gap-1 transition-colors",
              )}
              style={isActive ? { color: "#fff" } : { color: "rgba(255,255,255,0.5)" }}
            >
              {isActive && (
                <span
                  className="absolute top-0 rounded-b-full"
                  style={{
                    width: "28px",
                    height: "3px",
                    background:
                      "linear-gradient(90deg, #7C3AED 0%, #EC4899 100%)",
                  }}
                />
              )}
              <span className="text-xl leading-none">{tab.icon}</span>
              <span
                style={{
                  fontFamily: "Rubik, sans-serif",
                  fontWeight: 500,
                  fontSize: "0.65rem",
                }}
              >
                {tab.label}
              </span>
            </NavLink>
          );
        })}
      </nav>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </>
  );
}
